import {
  RESET_REDUX,
  RECEIVE_FACE_DETECTION,
  RECEIVE_PROTOCOL,
  SET_LOCATION_NOTE,
  CHANGE_LOCATION_MAP_COORDINATES,
  SET_TEMP_LOCATION_INCLUDED_MAPS,
  SET_CUSTOM_PROTOCOL_INFO,
  ADD_TEMP_LOCATION,
  SET_NEW_LOCATION,
  REMOVE_LOCATION,
  RESTORE_LOCATION,
  REMOVE_TEMP_LOCATION,
} from "../actions/index";
import produce from "immer";

import {
  customFaceMapId,
  faceMapId,
  circularNewLocRegion,
  tempLocationId,
  tempLocationSymmetricalId,
} from "../constants/constants";

import { getSymmetricCoordinates } from "../constants/utilityFunctions";
const initialState = {
  protocolId: null,
  protocolName: null,
  modified: false,
  locations: {},
  maps: {},
  acq_types: {},
  parameters: {},
};

export default (state = initialState, action) =>
  produce(state, (draft) => {
    switch (action.type) {
      case RECEIVE_PROTOCOL: {
        draft.protocolId = action.setProtocol.protocolId;
        draft.protocolName = action.setProtocol.protocolName;
        draft.locations = action.setProtocol.locations;
        draft.modified = action.setProtocol.modified;
        draft.maps = action.setProtocol.maps;
        draft.acq_types = action.setProtocol.acq_types;
        draft.parameters = action.setProtocol.parameters;
        if (Object.keys(action.setProtocol).includes("sws_range")) {
          draft.sws_range = action.setProtocol.sws_range;
        }
        break;
      }

      case RECEIVE_FACE_DETECTION: {
        // Add new map to body maps
        draft.maps[customFaceMapId] = {
          name: "Custom Face Map",
          image: action.image,
          filename:
            draft.protocolId +
            "_" +
            action.studyId +
            "_" +
            customFaceMapId +
            action.imageType,
        };
        // Add regions on each location
        Object.keys(action.regions).forEach((locationId) => {
          if (draft.locations[locationId]) {
            draft.locations[locationId].maps[customFaceMapId] =
              action.regions[locationId];
          }
        });
        //Transfer new location to the custom face as well
        Object.keys(draft.locations).forEach((locationId) => {
          if (!Object.keys(action.regions).includes(locationId)) {
            draft.locations[locationId].maps[customFaceMapId] =
              draft.locations[locationId].maps[faceMapId];
          }
        });
        break;
      }

      case SET_CUSTOM_PROTOCOL_INFO: {
        draft.protocolId = action.newProtocol.protocolId;
        draft.protocolName = action.newProtocol.protocolName;
        for (let map of Object.keys(action.newProtocol.maps)) {
          // create array of maps in custom protocol
          if (action.newProtocol.maps[map] === false) {
            delete draft.maps[map];
          }
        }
        break;
      }

      case ADD_TEMP_LOCATION: {
        // Add temporary locations. User is in "add new location" mode
        if (
          Object.keys(draft.locations).includes(tempLocationId) &&
          action.symmetry
        ) {
          // Create a symmetrric temp location
          let symmetricMapCoordinates = {};
          Object.keys(draft.locations[tempLocationId].maps).forEach((mapId) => {
            symmetricMapCoordinates[mapId] = getSymmetricCoordinates(
              draft.locations[tempLocationId].maps[mapId]
            );
          });
          draft.locations[tempLocationSymmetricalId] = {
            ...draft.locations[tempLocationId],
            name: "Temporary Location Symmetrical",
            maps: symmetricMapCoordinates,
          };
        } else {
          // Add an empty temp location
          let mapDict = {};
          for (let mapId of Object.keys(draft.maps)) {
            mapDict[mapId] = circularNewLocRegion;
          }
          let acqDict = {};
          for (let type in draft.acq_types) {
            acqDict[type] = 0;
          }
          draft.locations[tempLocationId] = {
            name: "Temporary Location",
            samples_needed: acqDict,
            z: 0.0075,
            dz: 0.001,
            new: true,
            notes: null,
            maps: mapDict,
            instruction: {
              text: null,
              illustration: [],
            },
            parameters_id: null,
          };
        }
        break;
      }

      case REMOVE_TEMP_LOCATION: {
        if (
          Object.keys(draft.locations).includes(tempLocationSymmetricalId) &&
          action.symmetry
        ) {
          delete draft.locations[tempLocationSymmetricalId];
        } else if (
          Object.keys(draft.locations).includes(tempLocationSymmetricalId) &&
          !action.symmetry
        ) {
          delete draft.locations[tempLocationId];
          delete draft.locations[tempLocationSymmetricalId];
        } else {
          delete draft.locations[tempLocationId];
        }
        break;
      }

      case SET_NEW_LOCATION: {
        // Add new location to protocol.
        draft.modified = true;
        Object.keys(action.newLocations).forEach((newLocId) => {
          draft.locations[action.newLocations[newLocId].locationId] = {
            name: action.newLocations[newLocId].locationName,
            samples_needed: action.newLocations[newLocId].samplesNeeded,
            z: 0.0075,
            dz: 0.001,
            maps: action.newLocations[newLocId].maps,
            new: true, //tag the location as an "added location"
            notes: null,
            instruction: action.newLocations[newLocId].instruction,
            parameters_id: action.newLocations[newLocId].parameterId,
          };
        });
        break;
      }

      case CHANGE_LOCATION_MAP_COORDINATES: {
        // Move location on map and change coordinates
        if (action.mapId === customFaceMapId) {
          // keep FACE_MAP and CUSTOM_FACE_MAP synced
          draft.locations[action.locId].maps[action.mapId] = action.coords;
          draft.locations[action.locId].maps[faceMapId] = action.coords;
          if (
            action.locId === tempLocationId &&
            Object.keys(draft.locations).includes(tempLocationSymmetricalId)
          ) {
            draft.locations[tempLocationSymmetricalId].maps[action.mapId] =
              getSymmetricCoordinates(action.coords);
            draft.locations[tempLocationSymmetricalId].maps[faceMapId] =
              getSymmetricCoordinates(action.coords);
          }
        } else {
          draft.locations[action.locId].maps[action.mapId] = action.coords;
          if (
            action.locId === tempLocationId &&
            Object.keys(draft.locations).includes(tempLocationSymmetricalId)
          ) {
            draft.locations[tempLocationSymmetricalId].maps[action.mapId] =
              getSymmetricCoordinates(action.coords);
          }
        }
        break;
      }

      case SET_TEMP_LOCATION_INCLUDED_MAPS: {
        // Toggle included maps for the temp location. User is in "add new location" mode
        draft.locations[action.locId].maps = action.mapSet;
        if (Object.keys(draft.locations).includes(tempLocationSymmetricalId)) {
          let symmCords = {};
          Object.keys(action.mapSet).forEach((mapId) => {
            symmCords[mapId] = getSymmetricCoordinates(action.mapSet[mapId]);
          });
          draft.locations[tempLocationSymmetricalId].maps = symmCords;
        }
        break;
      }

      case REMOVE_LOCATION: {
        draft.modified = true;
        draft.locations[action.locationId].removed = true;
        break;
      }

      case RESTORE_LOCATION: {
        draft.modified = false;
        draft.locations[action.locationId].removed = false;
        for (let locationId in draft.locations) {
          if (
            draft.locations[locationId].removed ||
            draft.locations[locationId].new
          ) {
            draft.modified = true;
            break;
          }
        }
        break;
      }

      case SET_LOCATION_NOTE: {
        // Add/update location note
        draft.locations[action.locationId].notes = action.note;
        break;
      }

      case RESET_REDUX:
        return initialState;
      default:
        return draft;
    }
  });
